import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { ChevronLeft, ChevronRight, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  validation: any;
  options: any[];
  step: number;
  order_position: number;
  score_weight: number;
  settings: any;
}

interface FormData {
  [key: string]: any;
}

const LeadForm: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const queryClient = useQueryClient();

  // Fetch form fields
  const { data: fields, isLoading } = useQuery({
    queryKey: ['formFields', formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_form_fields')
        .select('*')
        .eq('form_id', formId)
        .order('step', { ascending: true })
        .order('order_position', { ascending: true });

      if (error) throw error;
      return data as FormField[];
    }
  });

  // Submit form mutation
  const submitForm = useMutation({
    mutationFn: async (data: FormData) => {
      const { data: lead, error } = await supabase
        .from('leads')
        .insert({
          form_id: formId,
          data: data,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          company: data.company,
          job_title: data.job_title
        })
        .select()
        .single();

      if (error) throw error;

      await supabase
        .from('lead_activities')
        .insert({
          lead_id: lead.id,
          type: 'form_submission',
          description: 'Lead form submitted',
          score_change: calculateInitialScore(data)
        });

      return lead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Form submitted successfully!');
    },
    onError: (error) => {
      toast.error('Failed to submit form');
      console.error('Form submission error:', error);
    }
  });

  const calculateInitialScore = (data: FormData): number => {
    let score = 0;
    fields?.forEach(field => {
      if (data[field.name] && field.score_weight) {
        score += field.score_weight;
      }
    });
    return score;
  };

  const validateStep = (step: number): boolean => {
    const stepFields = fields?.filter(f => f.step === step) || [];
    const newErrors: {[key: string]: string} = {};
    let isValid = true;

    stepFields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = 'This field is required';
        isValid = false;
      }

      if (field.type === 'email' && formData[field.name]) {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailRegex.test(formData[field.name])) {
          newErrors[field.name] = 'Invalid email address';
          isValid = false;
        }
      }

      if (field.type === 'tel' && formData[field.name]) {
        const phoneRegex = /^\+?[\d\s-()]{10,}$/;
        if (!phoneRegex.test(formData[field.name])) {
          newErrors[field.name] = 'Invalid phone number';
          isValid = false;
        }
      }

      if (field.validation && Object.keys(field.validation).length > 0) {
        // Custom validation logic here
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      await submitForm.mutateAsync(formData);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      value: formData[field.name] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [field.name]: e.target.value });
      },
      className: `block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${
        errors[field.name] ? 'border-red-300' : ''
      }`,
      required: field.required
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <input
            type={field.type}
            {...commonProps}
          />
        );

      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={4}
          />
        );

      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            {field.options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={formData[field.name] === option.value}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentFields = fields?.filter(f => f.step === currentStep) || [];
  const totalSteps = Math.max(...(fields?.map(f => f.step) || [1]));

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round((currentStep / totalSteps) * 100)}% Complete
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-2 bg-primary rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {currentFields.map((field) => (
          <div key={field.id}>
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="mt-1">
              {renderField(field)}
            </div>
            {errors[field.name] && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors[field.name]}
              </p>
            )}
          </div>
        ))}

        <div className="flex justify-between pt-4">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="btn btn-outline"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </button>
          )}
          
          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-primary ml-auto"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-primary ml-auto"
              disabled={submitForm.isLoading}
            >
              {submitForm.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Submit
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default LeadForm;